import helpers from "@/helpers/global";

// The signup page checks whether an email already has an account before
// asking the server for a code. Starting that read when the person leaves the
// email field (and reusing it on submit) takes it off the click-to-code wait.
// Results are kept briefly so a slow typist never sees an hour-old answer.
const CHECK_TTL_MS = 60_000;

type Check = { promise: Promise<boolean>; startedAt: number };

const checks = new Map<string, Check>();

let readUserRecord = async (emailKey: string): Promise<unknown> => {
  const { getDatabase, ref, get } = await import("firebase/database");
  return (await get(ref(getDatabase(), `users/${emailKey}`))).val();
};

function emailKeyFor(email: string): string {
  return helpers.filterPath(helpers.filterEmail(email));
}

export function isSignupEmailRegistered(email: string): Promise<boolean> {
  const emailKey = emailKeyFor(email);
  const cached = checks.get(emailKey);
  if (cached && Date.now() - cached.startedAt < CHECK_TTL_MS) {
    return cached.promise;
  }

  const promise = readUserRecord(emailKey).then(
    (user) => !!user,
    (error) => {
      // A failed read must not be reused; the next call tries again.
      checks.delete(emailKey);
      throw error;
    }
  );
  checks.set(emailKey, { promise, startedAt: Date.now() });
  return promise;
}

// Warm-up only: errors surface again when the submit path awaits the check.
export function prefetchSignupEmailCheck(email: string): void {
  isSignupEmailRegistered(email).catch(() => undefined);
}

// Load the database SDK before it is needed, so the first check does not wait
// on the download.
export function preloadSignupEmailCheck(): void {
  import("firebase/database").catch(() => undefined);
}

export function resetSignupEmailChecksForTests(
  reader?: (emailKey: string) => Promise<unknown>
): void {
  checks.clear();
  if (reader) {
    readUserRecord = reader;
  }
}
